import { Strategy } from '../types'

export const strategies: Strategy[] = [
  {
    id: 'ai-tech-growth',
    title: 'AI & Technology Growth',
    tickers: 'NVDA, MSFT, GOOGL, SNOW, PLTR, ASML, TSLA',
    iconName: 'Cpu',
    strategyType: 'Momentum & Adaptive Weighting',
    rebalancing: 'Monthly',
    objective:
      'Maximize gains by focusing on high-growth stocks with strong momentum while adjusting exposure based on market trends.',
    rules: [
      {
        title: 'Momentum Score (MS)',
        description:
          'MS = (3-month + 6-month return) / volatility. Assets with the highest MS get more weight. If MS < 0, reduce exposure by 25-50%.',
      },
      {
        title: 'Profit-Taking Rule',
        description:
          'If TSLA or NVDA rises >30% in a month, take partial profit (10-20%). If Relative Strength Index (RSI) > 80, trim position.',
      },
      {
        title: 'Stop-Loss Rule',
        description:
          'If price falls 12% from the last peak, reduce exposure. If the 50-day MA crosses below the 200-day MA, remove the stock temporarily.',
      },
      {
        title: 'Risk-Based Weighting',
        formula: 'W_i = 1 / σ_i',
        description:
          'More weight to lower-volatility stocks like MSFT and GOOGL. Less weight to high-volatility stocks like TSLA and PLTR.',
      },
    ],
  },
  {
    id: 'stargate-500b',
    title: 'Stargate $500B',
    tickers: 'ORCI, NVDA, ARM, MSFT, SFTBY',
    iconName: 'Rocket',
    strategyType: 'Equal-Weight & Volatility-Based Rebalancing',
    rebalancing: 'Quarterly',
    objective:
      'Capture upside from semiconductor and AI megatrends while reducing risk from high volatility.',
    rules: [
      {
        title: 'Volatility-Adjusted Position Sizing',
        formula: 'W_i = (1 / σ_i) × (1 / Σ(1 / σ))',
        description:
          'Inverse volatility weighting. More weight to MSFT and ARM (lower volatility). Less weight to NVDA and ORCI (higher volatility).',
      },
      {
        title: 'Profit-Taking Rule',
        description:
          'If NVDA or ARM up 40% in a quarter, rebalance to equal-weight.',
      },
      {
        title: 'Stop-Loss Rule',
        description:
          'If ORCI drops >15% in a month, reduce exposure by half. If MSFT volatility spikes 50% above its historical average, rebalance.',
      },
    ],
  },
  {
    id: 'green-energy',
    title: 'Green Energy & Sustainability',
    tickers: 'NEE, FSLR, PLUG, ENPH, ALB, TSLA',
    iconName: 'Zap',
    strategyType: 'Mean Reversion & Trend Following',
    rebalancing: 'Quarterly',
    objective:
      'Balance between growth stocks and defensive renewable energy players while leveraging cyclical trends.',
    rules: [
      {
        title: 'Price-to-Book (P/B) Rebalancing',
        description:
          'Stocks with P/B < industry median get higher weight in rebalancing.',
      },
      {
        title: 'Mean Reversion Strategy',
        description:
          'If PLUG or ENPH drops >20% in a quarter, add more. If TSLA RSI > 85, take profits.',
      },
      {
        title: 'Stop-Loss & Sector Trend',
        description:
          'If a stock falls below its 200-day MA, remove it. If Clean Energy ETF (ICLN) rises 10%+ in a month, increase exposure.',
      },
    ],
  },
  {
    id: 'healthcare-innovation',
    title: 'Healthcare Innovation',
    tickers: 'MRNA, BNTX, ISRG, TDOC, VRTX, UNH',
    iconName: 'Activity',
    strategyType: 'Momentum & Risk-Adjusted Positioning',
    rebalancing: 'Monthly',
    objective:
      'Prioritize biotech and medical innovation while reducing exposure to speculative bets.',
    rules: [
      {
        title: 'Momentum Score Strategy',
        formula: 'MS = 6-month return / σ',
        description: 'High MS → more weight. Low MS → less weight.',
      },
      {
        title: 'Profit-Taking & Stop-Loss',
        description:
          'If MRNA or BNTX rises >30% in a month, sell 20%. If a stock drops >18% from recent high, remove it.',
      },
      {
        title: 'Defensive Rotation',
        description: 'Shift towards UNH when biotech is underperforming.',
      },
    ],
  },
  {
    id: 'emerging-markets',
    title: 'Emerging Markets Tech',
    tickers: 'TSM, BABA, TCEHY, SE, MELI, RELIANCE',
    iconName: 'Globe',
    strategyType: 'Momentum & Stop-Loss Protection',
    rebalancing: 'Bi-Monthly',
    objective:
      'Capture growth in global tech while mitigating geopolitical risk.',
    rules: [
      {
        title: 'Moving Average Weighting',
        description:
          'If above both 50-day & 200-day MA → Overweight. If below both → Underweight.',
      },
      {
        title: 'Profit-Taking & Stop-Loss',
        description:
          'If TSM or MELI rises >50% in 3 months, rebalance. If a stock falls >20% from peak, trim exposure.',
      },
    ],
  },
  {
    id: 'real-assets',
    title: 'Real Assets & Commodities',
    tickers: 'GLD, BAM, PLD, XOM, COP, ET',
    iconName: 'Database',
    strategyType: 'Volatility-Based & Mean Reversion',
    rebalancing: 'Quarterly',
    objective: 'Hedge against inflation and market downturns.',
    rules: [
      {
        title: 'Inflation Data Rebalancing',
        description: 'If CPI > 5%, increase exposure to GLD & XOM.',
      },
      {
        title: 'Profit-Taking & Stop-Loss',
        description:
          'If gold (GLD) rises >15% in a quarter, rebalance. If oil stocks drop >10% in a month, trim allocation.',
      },
    ],
  },
  {
    id: 'defense-cybersecurity',
    title: 'Defense & Cybersecurity',
    tickers: 'LMT, RTX, NOC, CRWD, PANW',
    iconName: 'Shield',
    strategyType: 'Trend Following & Risk-Based Rebalancing',
    rebalancing: 'Monthly',
    objective:
      'Capture upside from geopolitical trends while managing drawdowns.',
    rules: [
      {
        title: 'Government Contracts Weighting',
        description: 'More weight to companies with increasing DoD contracts.',
      },
      {
        title: 'Profit-Taking & Stop-Loss',
        description:
          'If CRWD or PANW up 50% in 3 months, take profits. If a stock falls >15% from peak, trim exposure.',
      },
    ],
  },
  {
    id: 'going-nuclear',
    title: 'Going Nuclear',
    tickers: 'SMR, BWXT, LEU, CEG, VST, OKLO, CCJ',
    iconName: 'Hexagon',
    strategyType: 'Adaptive Weighting & Risk Management',
    rebalancing: 'Monthly',
    objective:
      'Benefit from the growing nuclear trend while managing sector volatility.',
    rules: [
      {
        title: 'Uranium Price Adaptive Weighting',
        description:
          'If uranium prices rise >10% in a month, overweight CCJ & LEU.',
      },
      {
        title: 'Profit-Taking & Stop-Loss',
        description:
          'If a stock rises >40% in a quarter, rebalance. If a stock falls >18% from peak, trim exposure.',
      },
    ],
  },
  {
    id: 'web3-blockchain',
    title: 'Web3 & Blockchain',
    tickers: 'COIN, SQ, NVDA, MSTR, AMD',
    iconName: 'Link',
    strategyType: 'Momentum & Stop-Loss Protection',
    rebalancing: 'Monthly',
    objective:
      'Capture upside in crypto/blockchain while managing extreme volatility.',
    rules: [
      {
        title: 'BTC & ETH Correlation Weighting',
        description:
          'If BTC is in a bull run (above 200-day MA) → Overweight COIN & MSTR. If BTC breaks below 50-day MA, reduce COIN exposure.',
      },
      {
        title: 'Profit-Taking & Stop-Loss',
        description:
          'If MSTR or COIN rises >50% in a month, take profits. If COIN or MSTR falls >25% from peak, reduce exposure.',
      },
    ],
  },
  {
    id: 'lobbying-copycat',
    title: 'Lobbying Contracts Copycat',
    tickers: 'Various',
    iconName: 'Landmark',
    strategyType: 'Congressional Contracts & Lobbying-Based Trading',
    rebalancing: 'Every 6 months + Event-driven',
    objective:
      'Mimic government contract winners based on congressional hearings and lobbying influence.',
    rules: [
      {
        title: 'Contract-Based Buying',
        description:
          'BUY stocks of companies that win contracts or are favorably mentioned in Congressional & Senatorial Committee Hearings. Track DoD, Energy, Healthcare, Infrastructure, and Defense.',
      },
      {
        title: 'Lobbying Influence Rule',
        formula: 'W_i = Lobbying Dollars Spent / Σ Lobbying Dollars',
        description:
          'Allocate weights based on relative lobbying spend compared to industry peers. If a company reduces lobbying by >30% YoY, consider trimming.',
      },
      {
        title: 'Profit-Taking & Stop-Loss',
        description:
          'If stock rises >50% within 6 months of contract award, rebalance. If government pulls a contract or fines the company, remove from portfolio.',
      },
    ],
  },
  {
    id: 'senatorial-copycat',
    title: 'Senatorial Committees Copycat',
    tickers: 'Various',
    iconName: 'Building2',
    strategyType: 'Legislation-Driven Investing',
    rebalancing: 'Quarterly',
    objective:
      'Invest in sectors with favorable legislation, subsidies, or regulatory relaxations driven by Senate Committees.',
    rules: [
      {
        title: 'Sector Exposure',
        description:
          'Track key committees. BUY stocks in sectors where committee activity & lobbying increase. (e.g., EV tax credits → overweight TSLA, RIVN).',
      },
      {
        title: 'Weighting Formula',
        formula: 'W_i = Sponsored Bills for Sector / Σ Total Bills Passed',
        description:
          'Companies in sectors with the most legislative backing get higher weights.',
      },
      {
        title: 'Profit-Taking & Stop-Loss',
        description:
          'If stock rises >40% post-bill passage, rebalance. If legislation is delayed/reversed, trim exposure.',
      },
      {
        title: 'Defensive Rotation',
        description:
          'Shift towards infrastructure and defense in times of political uncertainty.',
      },
    ],
  },
]
